import KybSignatureGridRepository from "@/core/kyb/repositories/KybSignatureGridRepository";
import {
  ActivateKybSignatureGridInput,
  ActivateKybSignatureGridOutcome,
  KybSignatureGrid,
  KybSignatureGridStatus,
  KybSignatureMfaChallenge,
  KybSignatureOperationType,
  KybSignatureRange,
  KybSignatureSimulation,
  KybSignatureSimulationInput,
  KybSignatureSimulationOutcome,
  ProposeKybSignatureGridInput,
  ProposeKybSignatureGridOutcome,
} from "@/core/kyb/entities/KybSignatureGrid";
import DefaultExceptionTypes from "@/core/exceptions/DefaultExceptionTypes";
import { RepositoryResult, RepositoryResultFactory } from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import { executeKybRequest, toKybException } from "../../http/services/kybRequest/kybRequest";
import kybSignatureGridEndpoints from "../../api/kybSignatureGridEndpoints/kybSignatureGridEndpoints";

type RangeDto = { minAmount: number; maxAmount: number | null; requiredSignatures: number };
type GridDto = {
  gridId: string;
  caseId: string;
  operationType: string;
  currency: string;
  status: KybSignatureGridStatus;
  ranges: RangeDto[];
  createdBy: string;
  createdAt: string;
  validatedBy: string | null;
  validatedAt: string | null;
};
type SimulationDto = {
  gridId: string;
  range: RangeDto;
  requiredSignatures: number;
  availableSignatories: number;
  quorumReachable: boolean;
  signers: { personId: string; counted: boolean; reason: string | null }[];
  quorumMet: boolean | null;
};

const mapRange = (dto: RangeDto): KybSignatureRange => ({
  minAmount: dto.minAmount,
  maxAmount: dto.maxAmount ?? undefined,
  requiredSignatures: dto.requiredSignatures,
});

const mapGrid = (dto: GridDto): KybSignatureGrid => ({
  gridId: dto.gridId,
  caseId: dto.caseId,
  operationType: dto.operationType,
  currency: dto.currency,
  status: dto.status,
  ranges: dto.ranges.map(mapRange),
  createdBy: dto.createdBy,
  createdAt: dto.createdAt,
  validatedBy: dto.validatedBy ?? undefined,
  validatedAt: dto.validatedAt ?? undefined,
});

const mapSimulation = (dto: SimulationDto): KybSignatureSimulation => ({
  gridId: dto.gridId,
  range: mapRange(dto.range),
  requiredSignatures: dto.requiredSignatures,
  availableSignatories: dto.availableSignatories,
  quorumReachable: dto.quorumReachable,
  signers: dto.signers.map((s) => ({ personId: s.personId, counted: s.counted, reason: s.reason ?? undefined })),
  quorumMet: dto.quorumMet ?? undefined,
});

const stringList = (value: unknown): string[] | null =>
  Array.isArray(value) && value.every((item): item is string => typeof item === "string") ? value : null;

/**
 * Les conflits d'une grille (400) sont une liste d'énoncés dans `data`. Le client HTTP ne restructure que les 409 :
 * un 400 arrive avec la réponse dans `body`, on lit donc aussi de ce côté.
 */
function extractConflicts(error: unknown): string[] | null {
  if (typeof error !== "object" || error === null) {
    return null;
  }
  const { data, body, status, code } = error as {
    data?: unknown;
    body?: { data?: unknown } | null;
    status?: unknown;
    code?: unknown;
  };
  if (status !== 400 && code !== "400") {
    return null;
  }
  const list = stringList(data) ?? stringList(body?.data);
  return list && list.length > 0 ? list : null;
}

const isNotFound = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) {
    return false;
  }
  const { status, code, type } = error as { status?: unknown; code?: unknown; type?: unknown };
  return status === 404 || code === "404" || type === DefaultExceptionTypes.NOT_FOUND;
};

export default class KybSignatureGridRepositoryBackend implements KybSignatureGridRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async listOperationTypes(caseId: string): Promise<RepositoryResult<KybSignatureOperationType[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<{ code: string; label: string }[]>>({
          url: kybSignatureGridEndpoints.operationTypes(caseId),
          withAuth: true,
        }),
      (dtos) => dtos.map((d) => ({ code: d.code, label: d.label })),
    );
  }

  async listGrids(caseId: string): Promise<RepositoryResult<KybSignatureGrid[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<GridDto[]>>({ url: kybSignatureGridEndpoints.grids(caseId), withAuth: true }),
      (dtos) => dtos.map(mapGrid),
    );
  }

  async proposeGrid(
    caseId: string,
    input: ProposeKybSignatureGridInput,
  ): Promise<RepositoryResult<ProposeKybSignatureGridOutcome>> {
    try {
      const response = await this.httpClient.post<ApiResponse<GridDto>, ProposeKybSignatureGridInput>({
        url: kybSignatureGridEndpoints.grids(caseId),
        body: input,
        withAuth: true,
      });
      return RepositoryResultFactory.success({ outcome: "proposed", grid: mapGrid(response.data) });
    } catch (error) {
      const conflicts = extractConflicts(error);
      if (conflicts) {
        return RepositoryResultFactory.success({ outcome: "conflicts", conflicts });
      }
      return RepositoryResultFactory.error(toKybException(error));
    }
  }

  async requestActivationMfa(caseId: string, gridId: string): Promise<RepositoryResult<KybSignatureMfaChallenge>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<{ sentTo: string; ttlMinutes: number; testCode?: string }>, undefined>({
          url: kybSignatureGridEndpoints.activationMfaChallenge(caseId, gridId),
          withAuth: true,
        }),
      (dto) => ({ sentTo: dto.sentTo, ttlMinutes: dto.ttlMinutes, testCode: dto.testCode }),
    );
  }

  async activateGrid(
    caseId: string,
    gridId: string,
    input: ActivateKybSignatureGridInput,
  ): Promise<RepositoryResult<ActivateKybSignatureGridOutcome>> {
    try {
      const response = await this.httpClient.post<ApiResponse<GridDto>, ActivateKybSignatureGridInput>({
        url: kybSignatureGridEndpoints.activation(caseId, gridId),
        body: input,
        withAuth: true,
      });
      return RepositoryResultFactory.success({ outcome: "activated", grid: mapGrid(response.data) });
    } catch (error) {
      const conflicts = extractConflicts(error);
      if (conflicts) {
        return RepositoryResultFactory.success({ outcome: "conflicts", conflicts });
      }
      return RepositoryResultFactory.error(toKybException(error));
    }
  }

  async simulate(
    caseId: string,
    input: KybSignatureSimulationInput,
  ): Promise<RepositoryResult<KybSignatureSimulationOutcome>> {
    try {
      const response = await this.httpClient.post<ApiResponse<SimulationDto>, KybSignatureSimulationInput>({
        url: kybSignatureGridEndpoints.simulation(caseId),
        body: input,
        withAuth: true,
      });
      return RepositoryResultFactory.success({ outcome: "simulated", simulation: mapSimulation(response.data) });
    } catch (error) {
      if (isNotFound(error)) {
        return RepositoryResultFactory.success({ outcome: "noActiveGrid" });
      }
      return RepositoryResultFactory.error(toKybException(error));
    }
  }
}
